import apiClient from './client';
import type {
  HetznerStatus,
  RemoteBackupFile,
  RestoreReport,
  BackupScheduleConfig,
} from '@genealogy-app/shared';

// ─── Lokales Backup (Download) ──────────────────────────────────────────────

/**
 * Startet einen Download des kompletten Backups (ZIP).
 * Verwendet window-Navigation, damit der Browser die Datei korrekt speichert.
 */
export async function downloadLocalBackup(): Promise<void> {
  const response = await apiClient.get<Blob>('/backup', {
    responseType: 'blob',
  });

  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  let filename = 'genealogy-backup.zip';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match?.[1]) filename = match[1];
  }

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

// ─── Restore aus lokaler Datei ──────────────────────────────────────────────

export interface RestoreResponse {
  ok: true;
  report: RestoreReport;
}

export async function restoreFromFile(file: File): Promise<RestoreResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<RestoreResponse>('/backup/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60 * 1000, // 10 min
  });
  return data;
}

// ─── Hetzner Storage Box ────────────────────────────────────────────────────

export async function fetchHetznerStatus(): Promise<HetznerStatus> {
  const { data } = await apiClient.get<HetznerStatus>('/backup/hetzner/status');
  return data;
}

export interface TestConnectionResponse {
  ok: true;
  remotePath: string;
}

export async function testHetznerConnection(): Promise<TestConnectionResponse> {
  const { data } = await apiClient.post<TestConnectionResponse>('/backup/hetzner/test', {}, {
    timeout: 30_000,
  });
  return data;
}

export async function listHetznerBackups(): Promise<RemoteBackupFile[]> {
  const { data } = await apiClient.get<{ backups: RemoteBackupFile[] }>('/backup/hetzner/list', {
    timeout: 30_000,
  });
  return data.backups;
}

export interface HetznerUploadResponse {
  ok: true;
  filename: string;
  size: number;
  remotePath: string;
  remoteSize: number;
}

export async function uploadBackupToHetzner(): Promise<HetznerUploadResponse> {
  const { data } = await apiClient.post<HetznerUploadResponse>(
    '/backup/hetzner/upload',
    {},
    {
      timeout: 60 * 60 * 1000, // 1h – große Backups können dauern
    },
  );
  return data;
}

export interface HetznerRestoreResponse {
  ok: true;
  filename: string;
  report: RestoreReport;
}

export async function restoreFromHetzner(filename: string): Promise<HetznerRestoreResponse> {
  const { data } = await apiClient.post<HetznerRestoreResponse>(
    `/backup/hetzner/restore/${encodeURIComponent(filename)}`,
    {},
    {
      timeout: 60 * 60 * 1000,
    },
  );
  return data;
}

export async function deleteHetznerBackup(filename: string): Promise<void> {
  await apiClient.delete(`/backup/hetzner/${encodeURIComponent(filename)}`);
}

// ─── Automatische Backups (Zeitplan) ────────────────────────────────────────

export async function fetchBackupSchedule(): Promise<BackupScheduleConfig> {
  const { data } = await apiClient.get<BackupScheduleConfig>('/backup/schedule');
  return data;
}

export type ScheduleUpdate = Partial<
  Pick<BackupScheduleConfig, 'enabled' | 'interval' | 'time' | 'dayOfWeek' | 'dayOfMonth' | 'keepLast'>
>;

export async function updateBackupSchedule(update: ScheduleUpdate): Promise<BackupScheduleConfig> {
  const { data } = await apiClient.put<BackupScheduleConfig>('/backup/schedule', update);
  return data;
}

export async function triggerBackupNow(): Promise<{ ok: true; config: BackupScheduleConfig }> {
  const { data } = await apiClient.post<{ ok: true; config: BackupScheduleConfig }>(
    '/backup/schedule/run',
    {},
    { timeout: 60 * 60 * 1000 },
  );
  return data;
}

// ─── Utility ────────────────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
